import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { withRouter, Redirect } from 'react-router-dom'

import Style from './IncidentFeed.module.css'
import Overview from './Overview'
import ThreatFeed from './threatFeed/ThreatFeed'
import ComingSoon from './ComingSoon'
import { isGlobalView as isGlobalViewName } from '../../utilities/config'
import { INCIDENT_FEED_SEGMENT } from '../../utilities/menuRegistry'
import { getUTCDate } from '../../utilities/datetime'
import {
  INCIDENT_FEED_LOOKBACK_DAYS,
  INCIDENT_FEED_WINDOW_LABEL,
} from '../../store/initialState/incidentFeedState'
import {
  getOverview,
  getPhishingDetections,
  getPagesScanned,
} from '../../store/entities/incidentFeed'

// ===========================================================================
// Incident Feed (PIXMC-6442 / PIXMC-6521).
//
// Route: /{customers|tenants}/:id/incident-feed/:tab — tabs are PATH SEGMENTS, not a
// query param.
// A query param would have avoided touching App.js, nav and globalViewDisabled, but that
// saving is already spent (this is a new page), three of the four gating mechanisms key
// on path segments, and decisively `navbar.renderSelectedCustomerData` returns a bare
// path — so with `?type=` switching customer would silently drop the tab.
//
// D-9: the whole navigable shell ships at once. Five of the six tabs are Coming Soon
// placeholders owned by their own stories; all six are navigable and none error.
// ===========================================================================

export const DEFAULT_TAB = 'overview'

export const INCIDENT_FEED_TABS = [
  { key: 'overview', label: 'Overview', implemented: true },
  { key: 'threat-feed', label: 'Threat Feed', implemented: true },
  { key: 'policy-violations', label: 'Policy Violations', ticket: 'PIXMC-6503' },
  { key: 'saas-usage', label: 'SaaS Usage', ticket: 'PIXMC-6502' },
  { key: 'credential-risks', label: 'Credential Risks', ticket: 'PIXMC-6504' },
  { key: 'user-activity', label: 'User Activity', ticket: 'PIXMC-6501' },
]

export const incidentFeedPath = (customerId, tab = DEFAULT_TAB, isGlobalView = false) =>
  `/${isGlobalView ? 'tenants' : 'customers'}/${customerId}/${INCIDENT_FEED_SEGMENT}/${tab}`

// Which prefix the current URL uses. Taken from the pathname rather than from
// selectedCustomer so in-page links stay consistent with the address bar: the two can
// disagree for a tick while navbar is redirecting after a customer switch, and a tab
// click during that window should not jump the user across prefixes.
const isTenantPath = (pathname) => (pathname || '').startsWith('/tenants/')

const IncidentFeed = (props) => {
  const { selectedCustomer } = props
  const customerId = props.match?.params?.id
  const tabParam = props.match?.params?.tab

  const activeTab = INCIDENT_FEED_TABS.find((t) => t.key === tabParam)
  const onTenantPath = isTenantPath(props.location?.pathname)

  // 🔴 These calls take their path id from the ROUTE but their cKey / defaultCustomerId /
  // organizationName from `customers.selected`, so they are only coherent while those two
  // agree — and on a hard refresh they do not.
  //
  // `getCustomerList` defaults the selection to the FREE customer when nothing is chosen
  // yet (store/entities/customers.js: `customers.selected = customers.selected ?? customer[0]`
  // where customer[0] is REACT_APP_FREE_CUSTOMER_ID). navbar.componentDidUpdate only
  // afterwards matches the URL and calls setCurrentCustomer with the real one. So a reload
  // of /customers/<X>/incident-feed/overview passes through a state where the route says X
  // and the selection says FREE, and firing there produced a request for X's path carrying
  // the free customer's cKey — a second, wrong set of three calls whose responses raced the
  // right ones and sometimes won.
  //
  // Requiring the two to agree suppresses that fire entirely, in both directions: the
  // intermediate render after a customer switch (new selection, old route) is skipped too.
  // Everything this screen sends now describes one customer.
  const customerMatchesRoute = Boolean(customerId) && selectedCustomer?.id === customerId

  // Only the Overview and Threat Feed tabs make calls — they share the overview donut
  // (dashboard/stats/{id}/overview/phishing-detections) and the phishing-detections
  // series. Loading aggregates while the user sits on a Coming Soon tab would be requests
  // for nothing on a stats-heavy backend.
  useEffect(() => {
    if (!customerMatchesRoute) return
    if (tabParam !== DEFAULT_TAB && tabParam !== 'threat-feed') return
    // The window is fixed at 30 days for this release — no range picker. Computed here
    // rather than held in the slice so it is derived fresh on every load: a module-level
    // constant would pin the window to whenever the bundle was first evaluated, and a
    // session left open overnight would keep querying yesterday's 30 days.
    const options = {
      startTime: getUTCDate(INCIDENT_FEED_LOOKBACK_DAYS),
      endTime: getUTCDate(0),
      cKey: selectedCustomer.key,
      // r=C/M assembly, identical to incidentCharts.jsx. Driven by the selected customer
      // rather than by the URL because that is what decides which rows the API returns:
      // r=M also carries defaultCustomerId, which selects the database. Both are
      // populated on the Global View entry (store/entities/customers.js), the same ones
      // the Chart View already sends to /events in Global View today.
      isGlobalView: isGlobalViewName(selectedCustomer.organizationName),
    }
    const defaultCustomerId = selectedCustomer.defaultCustomerId
    props.getOverview(customerId, options, defaultCustomerId)
    props.getPhishingDetections(customerId, options, defaultCustomerId)
    // Pages scanned feeds only the Overview's Daily activity grid; the Threat Feed tab
    // does not render it.
    if (tabParam === DEFAULT_TAB) props.getPagesScanned(customerId, options, defaultCustomerId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerMatchesRoute, customerId, selectedCustomer?.id, tabParam])

  // No tab in the URL, or one that does not exist: land on the Overview rather than
  // falling through to App.js's catch-all, which would bounce the user off the feature.
  if (!activeTab) {
    return <Redirect to={incidentFeedPath(customerId, DEFAULT_TAB, onTenantPath)} />
  }

  const handleTabClick = (tab) => {
    if (tab.key === activeTab.key) return
    props.history.push(incidentFeedPath(customerId, tab.key, onTenantPath))
  }

  return (
    <div className={Style.page}>
      <div className={Style.pageHeader}>
        <div>
          <h1 className={Style.pageTitle}>Incident Feed</h1>
          <p className={Style.pageSubtitle}>
            Organizational risk across phishing, policy, credential and SaaS signals.
          </p>
        </div>
      </div>

      <div className={Style.tabBar}>
        {INCIDENT_FEED_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`${Style.tabBtn} ${tab.key === activeTab.key ? Style.tabBtnActive : ''}`}
            onClick={() => handleTabClick(tab)}
          >
            {tab.label}
            {!tab.implemented && <span className={Style.tabBadge}>Soon</span>}
          </button>
        ))}
      </div>

      {activeTab.key === DEFAULT_TAB && (
        <Overview
          windowLabel={INCIDENT_FEED_WINDOW_LABEL}
          overview={props.overview}
          overviewLoading={props.overviewLoading}
          overviewError={props.overviewError}
          phishingDetections={props.phishingDetections}
          phishingDetectionsLoading={props.phishingDetectionsLoading}
          phishingDetectionsError={props.phishingDetectionsError}
          pagesScanned={props.pagesScanned}
          pagesScannedLoading={props.pagesScannedLoading}
          pagesScannedError={props.pagesScannedError}
        />
      )}
      {activeTab.key === 'threat-feed' && (
        <ThreatFeed windowLabel={INCIDENT_FEED_WINDOW_LABEL} />
      )}
      {activeTab.key !== DEFAULT_TAB && activeTab.key !== 'threat-feed' && (
        <ComingSoon
          variant="section"
          label={activeTab.label}
          note={`${activeTab.label} is not part of this release.`}
          ticket={activeTab.ticket}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  selectedCustomer: state.entities.customers.selected,
  overview: state.entities.incidentFeed.overview,
  overviewLoading: state.entities.incidentFeed.overviewLoading,
  overviewError: state.entities.incidentFeed.overviewError,
  phishingDetections: state.entities.incidentFeed.phishingDetections,
  phishingDetectionsLoading: state.entities.incidentFeed.phishingDetectionsLoading,
  phishingDetectionsError: state.entities.incidentFeed.phishingDetectionsError,
  pagesScanned: state.entities.incidentFeed.pagesScanned,
  pagesScannedLoading: state.entities.incidentFeed.pagesScannedLoading,
  pagesScannedError: state.entities.incidentFeed.pagesScannedError,
})

const mapDispatchToProps = (dispatch) => ({
  getOverview: (customerId, options, defaultCustomerId) =>
    dispatch(getOverview(customerId, options, defaultCustomerId)),
  getPhishingDetections: (customerId, options, defaultCustomerId) =>
    dispatch(getPhishingDetections(customerId, options, defaultCustomerId)),
  getPagesScanned: (customerId, options, defaultCustomerId) =>
    dispatch(getPagesScanned(customerId, options, defaultCustomerId)),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(IncidentFeed))
